import { Injectable } from '@angular/core';
import { BuilderNode } from '../interfaces/builder-screen.models';

export interface PropertyGroup {
  id: string;
  title: string;
  priority: 'essential' | 'common' | 'advanced';
  properties: string[];
  autoExpand: boolean;
  icon: string;
  description?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PropertyGrouperService {
  groupProperties(component: BuilderNode): PropertyGroup[] {
    const groups: PropertyGroup[] = [];
    const componentType = component.type;

    // Propriedades essenciais (sempre visíveis e expandidas)
    groups.push({
      id: 'essential',
      title: 'builderScreen.inspector.groups.essential.title',
      priority: 'essential',
      properties: this.getEssentialProperties(componentType),
      autoExpand: true,
      icon: 'setting',
      description: 'builderScreen.inspector.groups.essential.description',
    });

    // Propriedades de aparência/estilo
    if (this.hasStyleProperties(componentType)) {
      groups.push({
        id: 'appearance',
        title: 'builderScreen.inspector.groups.appearance.title',
        priority: 'common',
        properties: this.getStyleProperties(componentType),
        autoExpand: false,
        icon: 'bg-colors',
        description: 'builderScreen.inspector.groups.appearance.description',
      });
    }

    // Propriedades de layout
    if (this.hasLayoutProperties(componentType)) {
      groups.push({
        id: 'layout',
        title: 'builderScreen.inspector.groups.layout.title',
        priority: 'common',
        properties: this.getLayoutProperties(componentType),
        autoExpand: false,
        icon: 'layout',
        description: 'builderScreen.inspector.groups.layout.description',
      });
    }

    // Propriedades de mapeamento de banco de dados
    if (this.hasDatabaseProperties(componentType)) {
      groups.push({
        id: 'database',
        title: 'builderScreen.inspector.groups.database.title',
        priority: 'common',
        properties: this.getDatabaseProperties(componentType),
        autoExpand: false,
        icon: 'database',
        description: 'builderScreen.inspector.groups.database.description',
      });
    }

    // Propriedades de validação
    if (this.hasValidationProperties(componentType)) {
      groups.push({
        id: 'validation',
        title: 'builderScreen.inspector.groups.validation.title',
        priority: 'advanced',
        properties: this.getValidationProperties(componentType),
        autoExpand: false,
        icon: 'safety-certificate',
        description: 'builderScreen.inspector.groups.validation.description',
      });
    }

    // Propriedades avançadas (colapsadas por padrão)
    const advancedProps = this.getAdvancedProperties(componentType);
    if (advancedProps.length > 0) {
      groups.push({
        id: 'advanced',
        title: 'builderScreen.inspector.groups.advanced.title',
        priority: 'advanced',
        properties: advancedProps,
        autoExpand: false,
        icon: 'experiment',
        description: 'builderScreen.inspector.groups.advanced.description',
      });
    }

    return groups;
  }

  private getEssentialProperties(componentType: string): string[] {
    const baseEssential = ['label', 'placeholder', 'required'];
    
    switch (componentType) {
      case 'text':
      case 'textarea':
        return [...baseEssential];
      case 'number':
        return [...baseEssential, 'min', 'max', 'step'];
      case 'switch':
        return ['label', 'required', 'defaultValue'];
      case 'button':
        return ['text', 'buttonType', 'size', 'actionType'];
      case 'tabs':
        return ['tabs', 'defaultActiveTab'];
      case 'simpleTable':
      case 'table':
        return ['columns', 'dataSource', 'pagination'];
      default:
        return baseEssential;
    }
  }

  private getStyleProperties(componentType: string): string[] {
    const baseStyle = ['backgroundColor', 'textColor'];
    
    switch (componentType) {
      case 'button':
        return [...baseStyle, 'borderColor', 'hoverColor'];
      case 'tabs':
        return [...baseStyle, 'activeTabColor', 'borderColor'];
      case 'simpleTable':
      case 'table':
        return [...baseStyle, 'headerBackgroundColor', 'rowBackgroundColor', 'alternateRowColor', 'hoverColor'];
      default:
        return baseStyle;
    }
  }

  private getLayoutProperties(componentType: string): string[] {
    return ['col', 'row', 'colSpan', 'panel'];
  }

  private getDatabaseProperties(componentType: string): string[] {
    const basicComponents = ['text', 'textarea', 'number', 'switch'];
    return basicComponents.includes(componentType) ? ['entityName', 'fieldName'] : [];
  }

  private getValidationProperties(componentType: string): string[] {
    switch (componentType) {
      case 'text':
      case 'textarea':
        return ['minLength', 'maxLength', 'pattern'];
      case 'number':
        return ['precision'];
      default:
        return [];
    }
  }

  private getAdvancedProperties(componentType: string): string[] {
    switch (componentType) {
      case 'button':
        return ['icon', 'disabled', 'loading'];
      case 'text':
      case 'textarea':
        return ['readonly', 'autocomplete'];
      case 'number':
        return ['formatter', 'parser'];
      default:
        return [];
    }
  }

  private hasStyleProperties(componentType: string): boolean {
    return true; // Todos os componentes têm propriedades de estilo
  }

  private hasLayoutProperties(componentType: string): boolean {
    return true; // Todos os componentes têm propriedades de layout
  }

  private hasDatabaseProperties(componentType: string): boolean {
    const basicComponents = ['text', 'textarea', 'number', 'switch'];
    return basicComponents.includes(componentType);
  }

  private hasValidationProperties(componentType: string): boolean {
    const validatableComponents = ['text', 'textarea', 'number'];
    return validatableComponents.includes(componentType);
  }

  getPropertyDisplayName(propertyKey: string): string {
    // Retorna chaves de tradução em vez de strings em inglês hardcoded
    const translationKeys: Record<string, string> = {
      // Essencial
      'label': 'builderScreen.inspector.label',
      'placeholder': 'builderScreen.inspector.placeholder',
      'required': 'builderScreen.inspector.required',
      'text': 'builderScreen.button.properties.text',
      'buttonType': 'builderScreen.inspector.buttonType',
      'size': 'builderScreen.button.properties.size',
      'actionType': 'builderScreen.inspector.action',
      'defaultValue': 'builderScreen.inspector.defaultValue',
      
      // Estilo
      'backgroundColor': 'builderScreen.inspector.buttonColor',
      'textColor': 'builderScreen.inspector.color',
      'borderColor': 'builderScreen.inspector.borderColor',
      'hoverColor': 'builderScreen.inspector.hoverColor',
      'activeTabColor': 'builderScreen.inspector.activeTabColor',
      'headerBackgroundColor': 'builderScreen.inspector.headerBackgroundColor',
      'rowBackgroundColor': 'builderScreen.inspector.rowBackgroundColor',
      'alternateRowColor': 'builderScreen.inspector.alternateRowColor',
      
      // Layout
      'col': 'builderScreen.inspector.currentPosition',
      'row': 'builderScreen.inspector.currentPosition',
      'colSpan': 'builderScreen.inspector.colspan',
      'panel': 'builderScreen.inspector.panel',
      
      // Banco de dados
      'entityName': 'builderScreen.inspector.entityName',
      'fieldName': 'builderScreen.inspector.fieldName',
      
      // Validação
      'minLength': 'builderScreen.inspector.minLength',
      'maxLength': 'builderScreen.inspector.maxLength',
      'pattern': 'builderScreen.inspector.pattern',
      'min': 'builderScreen.inspector.min',
      'max': 'builderScreen.inspector.max',
      'step': 'builderScreen.inspector.step',
      'precision': 'builderScreen.inspector.precision',
      
      // Avançado
      'icon': 'builderScreen.inspector.icon',
      'disabled': 'builderScreen.inspector.disabled',
      'loading': 'builderScreen.inspector.loading',
      'readonly': 'builderScreen.inspector.readonly',
      'autocomplete': 'builderScreen.inspector.autocomplete',
      'formatter': 'builderScreen.inspector.formatter',
      'parser': 'builderScreen.inspector.parser',
    };

    return translationKeys[propertyKey] || `builderScreen.inspector.${propertyKey}`;
  }

  getPropertyDescription(propertyKey: string): string {
    const descriptions: Record<string, string> = {
      'entityName': 'Database table name for data mapping',
      'fieldName': 'Database column name for data mapping',
      'actionType': 'Defines what happens when button is clicked',
      'required': 'Makes this field mandatory for form submission',
      'pattern': 'Regular expression for input validation',
      'precision': 'Number of decimal places for numeric input',
    };

    return descriptions[propertyKey] || '';
  }
}
